import { useEffect, useId, useRef } from 'react'
import { useI18n } from '../i18n.jsx'
import { CarouselIndicators, useMobileCarousel } from './MobileCarousel.jsx'
import { useMobileCardHeight } from './useMobileCardHeight.js'
import './hardware-design.css'

function useCommunicationMotion() {
  const artwork = useRef(null)
  useEffect(() => {
    const root = artwork.current
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
    let visible = false
    const synchronize = () => {
      root.dataset.communicationMotion = reducedMotion.matches ? 'reduced' : visible && !document.hidden ? 'running' : 'paused'
    }
    const intersection = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting && entry.intersectionRatio >= .05
      synchronize()
    }, { threshold: .05 })
    intersection.observe(root)
    document.addEventListener('visibilitychange', synchronize)
    reducedMotion.addEventListener('change', synchronize)
    synchronize()
    return () => {
      intersection.disconnect()
      document.removeEventListener('visibilitychange', synchronize)
      reducedMotion.removeEventListener('change', synchronize)
    }
  }, [])
  return artwork
}

function CommunicationFlow({ routes }) {
  return <g className="hardware-communication-flow" aria-hidden="true" pointerEvents="none">
    {routes.map(([d, duration, delay], index) => <g key={d} data-flow-route={index} style={{ '--flow-duration': `${duration}s`, '--flow-delay': `${delay}s` }}>
      {[3, 2, 1, 0].map(segment => <path key={segment} d={d} pathLength="100" className={`hardware-flow-segment hardware-flow-segment-${segment}`} style={{ '--flow-lag': `${segment * 3}px` }} />)}
    </g>)}
  </g>
}

function OnboardChipDrawing({ label }) {
  const artwork = useCommunicationMotion()
  return <svg ref={artwork} data-communication-motion="paused" className="hardware-chip-drawing" viewBox="0 0 360 240" fill="none" role="img" aria-label={label}>
    <path d="M30 30h300v180H30z" fill="#081a25" className="hardware-muted-line" />
    <path d="M38 38h284v164H38z" className="hardware-fine-line" />
    {[ [44, 44], [316, 44], [44, 196], [316, 196] ].map(([x, y]) => <g key={`${x}-${y}`}><circle cx={x} cy={y} r="5" /><circle cx={x} cy={y} r="2" /></g>)}
    <g className="hardware-traces">
      <path d="M122 91H95L73 69H52m70 30H91L69 77H52m70 30H87L65 85H52" />
      <path d="M238 103h23l21-21h27m-71 29h27l21-21h23m-71 30h31l21-21h19" />
      <path d="M163 153v17l-20 20h-39m67-37v21l-20 20h-47m83-41v19l22 22h55m-47-41v15l22 22h25" />
      <path d="M164 77V55h-40m48 22V47h-48m75 30V58h29" />
    </g>
    <rect x="122" y="77" width="116" height="76" rx="3" fill="#09141e" />
    <rect x="135" y="90" width="90" height="50" rx="2" className="hardware-blue-line" />
    <rect x="160" y="103" width="40" height="25" rx="1" className="hardware-fine-line" />
    <circle cx="130" cy="85" r="2" className="hardware-blue-line" />
    {Array.from({ length: 12 }, (_, i) => <path key={`pins-v-${i}`} d={`M${129 + i * 9} 69v8m0 76v8`} />)}
    {Array.from({ length: 7 }, (_, i) => <path key={`pins-h-${i}`} d={`M114 ${84 + i * 10}h8m116 0h8`} />)}
    {[ [69, 131], [88, 131], [267, 144], [286, 144] ].map(([x, y]) => <g key={`${x}-${y}`}><rect x={x} y={y} width="10" height="22" rx="1" /><path d={`M${x} ${y + 5}h10m-10 12h10`} className="hardware-blue-line" /></g>)}
    <rect x="92" y="44" width="31" height="18" rx="2" /><path d="M97 49h21v8H97z" className="hardware-fine-line" />
    <rect x="227" y="46" width="43" height="21" rx="2" /><path d="M231 52h35m-35 9h35" className="hardware-fine-line" />
    <path d="M80 174h20v15H80zM263 172h34v17h-34z" />
    {[ [73, 69], [69, 77], [65, 85], [282, 82], [286, 90], [290, 99], [143, 190], [151, 194], [219, 194], [231, 190] ].map(([x, y]) => <circle key={`${x}-${y}`} cx={x} cy={y} r="2" className="hardware-blue-line" />)}
    <CommunicationFlow routes={[
      ['M52 69H73L95 91H122', 2.1, -.4],
      ['M238 103h23l21-21h27', 1.8, -1.2],
      ['M164 77V55h-40', 2.4, -1.8],
    ]} />
  </svg>
}

function AntennaDrawing({ label }) {
  const artwork = useCommunicationMotion()
  const gridId = useId().replaceAll(':', '')
  return <svg ref={artwork} data-communication-motion="paused" className="hardware-board-drawing hardware-antenna-drawing" viewBox="0 -16 360 376" fill="none" role="img" aria-label={label}>
    <defs><pattern id={gridId} width="20" height="20" patternUnits="userSpaceOnUse"><path d="M20 0H0v20" stroke="#77b8dd" strokeOpacity=".055" strokeWidth=".6" /></pattern></defs>
    <rect y="-16" width="360" height="376" fill={`url(#${gridId})`} stroke="none" />
    <g transform="translate(37 37)">
      <rect x="0" y="0" width="286" height="286" rx="14" className="hardware-muted-line" />
      <rect x="10" y="10" width="266" height="266" rx="9" className="hardware-fine-line" />
      <rect x="27" y="32" width="232" height="215" rx="5" fill="#081a25" className="hardware-board-outline" />
      <rect x="37" y="41" width="165" height="64" rx="3" className="hardware-keepout" />
      <path d="M47 91V54h25v32h24V54h24v32h24V54h25v32h22V54" className="hardware-antenna-trace" />
      <path d="M47 91v37h61v31h13" className="hardware-antenna-trace" />
      <path d="M57 107v14h57v30m-77-44v28h65v32" className="hardware-fine-line" />
      <g className="hardware-components">
        <rect x="121" y="132" width="65" height="58" rx="3" fill="#09141e" />
        <rect x="132" y="143" width="43" height="36" rx="1" className="hardware-blue-line" />
        {Array.from({ length: 7 }, (_, i) => <path key={i} d={`M${128 + i * 8} 124v8m0 58v8M113 ${139 + i * 7}h8m65 0h8`} />)}
        <circle cx="128" cy="139" r="2" />
        <rect x="216" y="121" width="23" height="53" rx="2" />
        <path d="M221 129h13m-13 8h13m-13 8h13m-13 8h13m-13 8h13" className="hardware-fine-line" />
        <rect x="59" y="192" width="31" height="23" rx="2" /><path d="M64 197h21v13H64z" className="hardware-blue-line" />
        {[ [104, 207], [125, 207], [208, 192], [230, 192] ].map(([x, y]) => <g key={`${x}-${y}`}><rect x={x} y={y} width="9" height="19" rx="1" /><path d={`M${x} ${y + 4}h9m-9 11h9`} className="hardware-blue-line" /></g>)}
      </g>
      <g className="hardware-traces"><path d="M194 148h14v-27m-14 34h20v-34M140 198v33H90m59-33v39H90m20-70H74v25m35-16H99v39m65-17v23h40m-31-23v17h31" /></g>
      {[ [35, 40], [250, 40], [35, 237], [250, 237] ].map(([x, y]) => <g key={`${x}-${y}`}><circle cx={x} cy={y} r="4" /><circle cx={x} cy={y} r="1.5" /></g>)}
      <path d="M102 260h81" className="hardware-muted-line" />
      <circle cx="100" cy="260" r="3" /><circle cx="143" cy="260" r="3" /><circle cx="185" cy="260" r="3" />
      <CommunicationFlow routes={[
        ['M47 91v37h61v31h13', 2.1, -.4],
        ['M194 148h14v-27', 1.8, -1.2],
        ['M140 198v33H90', 2.4, -1.8],
      ]} />
    </g>
    <g className="hardware-rf-waves"><path d="M139 26q21-13 42-3m-48-7q27-18 54-3m-60-7q33-22 65-3" /></g>
  </svg>
}

function BatteryDrawing({ label }) {
  return <svg className="hardware-battery-drawing" viewBox="0 0 360 240" fill="none" stroke="#ccdde6" strokeWidth="1.1" role="img" aria-label={label} data-art-style="engineering-linework">
    <rect x="34" y="64" width="274" height="112" rx="8" fill="#081a25" className="hardware-muted-line" />
    <rect x="44" y="74" width="254" height="92" rx="4" className="hardware-fine-line" />
    <path d="M308 101h17v38h-17" fill="#09141e" className="hardware-blue-line" />
    <path d="M313 107h7v26h-7" className="hardware-fine-line" />
    {[66, 120, 174, 228].map(x => <g key={x}>
      <rect x={x} y="94" width="40" height="52" rx="2" className="hardware-blue-line" />
      <path d={`M${x + 6} 100h28m-28 40h28M${x + 6} 106v28m28-28v28`} className="hardware-fine-line" />
    </g>)}
    <path d="M56 88v-8h18m194 0h18v8M56 152v8h18m194 0h18v-8" className="hardware-fine-line" />
    <path d="M54 120h7m219 0h7" className="hardware-blue-line" />
  </svg>
}

export default function HardwareDesign() {
  const { t, language } = useI18n()
  const title = t('site.hardware_title')
  const track = useRef(null)
  const carousel = useMobileCarousel(track)
  useMobileCardHeight(track, language, 2 / 3, '.hardware-card-copy', '.hardware-artwork')
  return <section id="partnership" className="section hardware-section uniform-section">
    <div className="section-inner hardware-layout">
      <div className="hardware-heading">
        <div><h2>{title}</h2><p>{t('site.hardware_description')}</p></div>
      </div>
      <div className="hardware-grid mobile-carousel-track" ref={track}>
        <article className="hardware-panel hardware-chip-panel" tabIndex={0}>
          <div className="hardware-card-copy">
            <h3>{t('site.hardware_chip_title')}</h3>
            <p>{t('site.hardware_chip_description')}</p></div>
          <figure className="hardware-artwork"><OnboardChipDrawing label={t('site.hardware_chip_title')} /></figure>
        </article>
        <article className="hardware-panel hardware-board-panel" tabIndex={0}>
          <div className="hardware-card-copy">
            <h3>{t('site.hardware_antenna_title')}</h3>
            <p>{t('site.hardware_antenna_description')}</p>
          </div>
          <figure className="hardware-artwork">
            <AntennaDrawing label={t('site.hardware_antenna_alt')} />
          </figure>
        </article>
        <article className="hardware-panel hardware-power-panel" tabIndex={0}>
          <div className="hardware-card-copy">
            <h3>{t('site.hardware_power_title')}</h3>
            <p>{t('site.hardware_power_description')}</p>
          </div>
          <figure className="hardware-artwork"><BatteryDrawing label={t('site.hardware_power_title')} /></figure>
        </article>
      </div>
      <CarouselIndicators carousel={carousel} title={title} labels={['chip', 'antenna', 'power'].map(part => t(`site.hardware_${part}_title`))} />
    </div>
  </section>
}
